import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'wz_cookie_consent';
const RESHOW_DAYS = 180;
const RESHOW_MS = RESHOW_DAYS * 24 * 60 * 60 * 1000;

// A lightweight, bottom-of-screen cookie consent bar. The user's choice is
// stored in localStorage with a timestamp; the bar reappears only once the
// choice is older than RESHOW_DAYS (180 days). Rendering is gated on a mount
// effect so it is never baked into the prerendered static HTML.
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Don't render during the Puppeteer prerender pass, otherwise the bar gets
    // baked into the static HTML and flashes for visitors who already consented.
    if (navigator.webdriver) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) { setVisible(true); return; }
      const { ts } = JSON.parse(raw);
      if (!ts || Date.now() - ts > RESHOW_MS) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  function record(choice) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice, ts: Date.now() }));
    } catch { /* storage blocked — just close the bar */ }
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1080,
        background: '#1e1b4b', color: '#fff',
        borderTop: '2px solid #4f46e5',
        padding: '0.6rem 1rem',
        boxShadow: '0 -2px 12px rgba(0,0,0,0.18)',
      }}
    >
      <div
        className="container-xl"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexWrap: 'wrap', gap: '0.6rem 1rem',
          fontFamily: 'Nunito, sans-serif',
        }}
      >
        <span style={{ fontSize: '0.85rem', lineHeight: 1.5, flex: '1 1 260px', minWidth: 0 }}>
          🍪 We use cookies to run WordZingo, measure traffic, and show ads.{' '}
          <Link to="/privacy" style={{ color: '#a5b4fc', textDecoration: 'underline' }}>
            Learn more
          </Link>
        </span>
        <div style={{ display: 'flex', gap: '0.5rem', flex: '0 0 auto' }}>
          <button
            type="button"
            onClick={() => record('rejected')}
            style={{
              background: 'transparent', color: '#c7d2fe',
              border: '1px solid #4f46e5', borderRadius: 50,
              padding: '0.3rem 1rem', fontSize: '0.82rem', fontWeight: 700,
              cursor: 'pointer', fontFamily: 'Nunito, sans-serif',
            }}
          >
            Reject
          </button>
          <button
            type="button"
            onClick={() => record('accepted')}
            style={{
              background: '#a5b4fc', color: '#1e1b4b',
              border: 'none', borderRadius: 50,
              padding: '0.3rem 1.25rem', fontSize: '0.82rem', fontWeight: 800,
              cursor: 'pointer', fontFamily: 'Nunito, sans-serif',
            }}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
